/**
 * Questions d'exercices reliées à une SKILLCARD (migration 0271).
 *
 * Une bonne réponse à une question reliée verse l'XP de la question à la carte
 * (crédit serveur dans `finish_attempt`). On relie la QUESTION : un bloc V2
 * `statement_question` (le serveur remonte du checkpoint `correction`
 * enregistré à sa question parente). Zéro V1 : plus de lien `step_id`.
 * RLS : seul l'auteur de la carte relie, et seulement ses propres exercices
 * (ou n'importe lequel pour un admin).
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { SupabaseClient } from '@supabase/supabase-js';
import { supabase } from '@/lib/supabase';

const db = supabase as unknown as SupabaseClient;

export type CardQuestionRole = 'principal' | 'secondaire';

export interface CardQuestionLink {
  id: string;
  exercise_id: string;
  exercise_title: string;
  block_id: string;
  role: CardQuestionRole;
}

export interface ExerciseQuestion {
  /** id du bloc statement_question. */
  id: string;
  index: number;
  label: string;
}

export const cardQuestionLinksKey = (cardId: string) => ['card-question-links', cardId] as const;

export function useCardQuestionLinks(cardId: string) {
  return useQuery({
    queryKey: cardQuestionLinksKey(cardId),
    queryFn: async (): Promise<CardQuestionLink[]> => {
      const { data, error } = await db
        .from('card_question_links')
        .select('id, exercise_id, block_id, role, exercises(title)')
        .eq('card_id', cardId)
        .order('created_at');
      if (error) throw new Error(error.message);
      type Row = Omit<CardQuestionLink, 'exercise_title'> & { exercises: { title: string } | null };
      return ((data ?? []) as unknown as Row[]).map(({ exercises, ...rest }) => ({
        ...rest,
        exercise_title: exercises?.title ?? 'Exercice',
      }));
    },
  });
}

/** Premier texte non vide parmi des champs candidats (blocs seed/IA hétérogènes). */
function firstText(data: Record<string, unknown> | null, keys: string[]): string {
  for (const k of keys) {
    const v = data?.[k];
    if (typeof v === 'string' && v.trim()) return v.trim();
  }
  return '';
}

/** Libellé court d'une question (une ligne, sans syntaxe markdown lourde). */
export function questionLabel(text: string, index: number): string {
  const flat = text
    .replace(/\s+/g, ' ')
    .replace(/[#*_`]/g, '')
    .trim();
  const base = flat || `Question ${index + 1}`;
  return base.length > 110 ? `${base.slice(0, 107)}…` : base;
}

export function useExerciseQuestions(exerciseId: string | null) {
  return useQuery({
    queryKey: ['exercise-questions', exerciseId],
    enabled: !!exerciseId,
    queryFn: async (): Promise<ExerciseQuestion[]> => {
      const { data, error } = await db
        .from('exercise_blocks')
        .select('id, data')
        .eq('exercise_id', exerciseId as string)
        .eq('block_type', 'statement_question')
        .order('position');
      if (error) throw new Error(error.message);
      return ((data ?? []) as { id: string; data: Record<string, unknown> | null }[]).map(
        (b, i) => ({
          id: b.id,
          index: i,
          label: questionLabel(firstText(b.data, ['prompt_md', 'markdown', 'statement_md']), i),
        }),
      );
    },
  });
}

export function translateLinkError(message: string): string {
  if (message.includes('row-level security')) {
    return 'Tu ne peux relier que les questions de tes propres exercices.';
  }
  if (message.includes('duplicate key')) return 'Cette question est déjà reliée à la carte.';
  return message;
}

export function useAddCardQuestionLink(cardId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      exerciseId,
      question,
      role,
    }: {
      exerciseId: string;
      question: ExerciseQuestion;
      role: CardQuestionRole;
    }) => {
      const { error } = await db.from('card_question_links').insert({
        card_id: cardId,
        exercise_id: exerciseId,
        block_id: question.id,
        role,
      });
      if (error) throw new Error(translateLinkError(error.message));
    },
    onSettled: () => {
      qc.invalidateQueries({ queryKey: cardQuestionLinksKey(cardId) });
      qc.invalidateQueries({ queryKey: ['exercise-cards'] });
    },
  });
}

export function useRemoveCardQuestionLink(cardId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (linkId: string) => {
      const { error } = await db.from('card_question_links').delete().eq('id', linkId);
      if (error) throw new Error(error.message);
    },
    onSettled: () => {
      qc.invalidateQueries({ queryKey: cardQuestionLinksKey(cardId) });
      qc.invalidateQueries({ queryKey: ['exercise-cards'] });
    },
  });
}

export interface ExerciseCardChip {
  card_id: string;
  title: string;
  question_count: number;
}

/** Cartes (dont je suis l'auteur, RLS) reliées à au moins une question de l'exercice. */
export function useExerciseCards(exerciseId: string) {
  return useQuery({
    queryKey: ['exercise-cards', exerciseId],
    queryFn: async (): Promise<ExerciseCardChip[]> => {
      const { data, error } = await db
        .from('card_question_links')
        .select('card_id, cards(title)')
        .eq('exercise_id', exerciseId);
      if (error) throw new Error(error.message);
      const byCard = new Map<string, ExerciseCardChip>();
      for (const row of (data ?? []) as unknown as {
        card_id: string;
        cards: { title: string } | null;
      }[]) {
        const prev = byCard.get(row.card_id);
        if (prev) prev.question_count += 1;
        else
          byCard.set(row.card_id, {
            card_id: row.card_id,
            title: row.cards?.title ?? 'Carte',
            question_count: 1,
          });
      }
      return [...byCard.values()];
    },
  });
}
